
import Link from 'next/link'
import { Icon } from '@/components/ui/Icon'
import type { BuiltPowerPlan, PowerPlanSnapshot } from '@/lib/power-plan'
import { btn, card, cx } from '@/lib/ui'
import type { PowerCanvas, PowerDevicePick } from './PowerPlanClient'

/**
 * The plan against the drawing, read-only in one direction.
 *
 * Re-syncing adds what the canvas has and the plan lacks. It never edits a
 * hand-typed row, never touches a wattage somebody overrode, and can never
 * create, move or delete a canvas node. One-way, always: that keeps the plan
 * auditable and keeps the blast radius of a stored JSON snapshot at zero.
 */
export function PowerReconcilePanel({
  canvas,
  snapshot,
  built,
  devices,
  basePath,
  onChange,
}: {
  canvas: PowerCanvas
  snapshot: PowerPlanSnapshot
  built: BuiltPowerPlan
  devices: Map<string, PowerDevicePick>
  basePath: string
  onChange: (next: (current: PowerPlanSnapshot) => PowerPlanSnapshot) => void
}) {
  const plannedDeviceIds = new Set(
    snapshot.items.map((item) => item.deviceId).filter((id): id is string => Boolean(id)),
  )
  const missing = canvas.nodes.filter((node) => !plannedDeviceIds.has(node.deviceId))
  const canvasDeviceIds = new Set(canvas.nodes.map((node) => node.deviceId))
  const extra = snapshot.items.filter(
    (item) => !item.deviceId || !canvasDeviceIds.has(item.deviceId),
  )

  // A redundant device whose cords both land on one circuit has no redundancy
  // left. Phrased against the object, because "check your feeds" is advice and
  // "this box loses power when that MCB trips" is a fact.
  const defeated = built.rows.filter(
    (row) => row.psuMode === 'REDUNDANT' && row.groupIds.length === 1,
  )

  const addMissing = () =>
    onChange((current) => {
      const seen = new Set(current.items.map((item) => item.deviceId).filter(Boolean))
      const additions = []
      for (const node of missing) {
        if (seen.has(node.deviceId)) continue
        seen.add(node.deviceId)
        const device = devices.get(node.deviceId)
        additions.push({
          id: `n-${node.id}`,
          kind: 'DEVICE' as const,
          deviceId: node.deviceId,
          label: device ? `${device.manufacturer} ${device.model}` : node.label,
          qty: canvas.nodes.filter((candidate) => candidate.deviceId === node.deviceId).length,
          groupIds: [],
          catalogueWattsAtSnapshot: device?.powerDrawMaxW ?? null,
        })
      }
      return { ...current, items: [...current.items, ...additions] }
    })

  return (
    <section className={cx(card, 'px-5 py-4')} aria-label="Against the canvas">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-medium">Against the canvas</h2>
        <Link
          href={`${basePath}/projects/${canvas.id}`}
          className="inline-flex items-center gap-1 text-[11px] text-muted underline underline-offset-2 hover:text-foreground"
        >
          {canvas.name}
          <Icon name="external" className="h-3 w-3" />
        </Link>
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-muted">
        {canvas.nodes.length} {canvas.nodes.length === 1 ? 'node' : 'nodes'} drawn ·{' '}
        {canvas.nodes.length - missing.length} on this plan
        {missing.length > 0 && ` · ${missing.length} missing`}
        {extra.length > 0 &&
          ` · ${extra.length} plan ${extra.length === 1 ? 'row is' : 'rows are'} not on the canvas (fine — LED wall, lighting, chargers)`}
      </p>

      {missing.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" onClick={addMissing} className={cx(btn.sm, btn.secondary)}>
            <Icon name="plus" className="h-3.5 w-3.5" strokeWidth={2} />
            Add {missing.length} missing {missing.length === 1 ? 'device' : 'devices'}
          </button>
          <span className="text-[11px] text-muted">
            {missing
              .slice(0, 4)
              .map((node) => node.label)
              .join(' · ')}
            {missing.length > 4 && ` and ${missing.length - 4} more`}
          </span>
        </div>
      )}

      {defeated.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-[var(--border)] pt-3">
          {defeated.map((row) => (
            <li key={row.id} className="text-[11px] text-warning">
              {row.label} has {row.psuCount} cords, both on one circuit. The redundancy is defeated.
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
